import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CIERRE_CHIP_CLICK_SLOP,
  CIERRE_CHIP_GAP,
  CIERRE_CHIP_H,
  CIERRE_CHIP_STEP,
  CIERRE_CHIP_W,
  carouselAnchorIndex,
  carouselChipIndexAtX,
  carouselMaxIndex,
  carouselPeekFlags,
  carouselPointerRelease,
  carouselShowChrome,
  carouselSlotLeft,
  carouselSnapIndex,
  carouselTranslateX,
  carouselViewportWidth,
  cierreChipLabel,
  cierreEstadoOf,
  clampNumber,
  type CierreDiaChip,
} from "./cierreCarousel";

type CierreDiasCarouselProps = {
  days: string[];
  today: string;
  selectedYmd: string;
  onSelect: (ymd: string) => void;
  cierres: CierreDiaChip[];
  sucursalId?: number | null;
};

function DiaChip({
  label,
  estado,
  active,
  preview,
}: {
  label: string;
  estado: "abierto" | "cerrado" | "sin";
  active: boolean;
  preview: boolean;
}) {
  const title = preview
    ? "Ventana al soltar"
    : estado === "cerrado"
      ? "Cierre cerrado"
      : estado === "abierto"
        ? "Cierre abierto"
        : "Sin cierre";
  return (
    <div
      title={title}
      className={cn(
        "box-border flex shrink-0 items-center justify-center rounded-md border-2 text-xs select-none",
        "transition-[background-color,border-color,color] duration-150 ease-out",
        active && "border-primary bg-primary font-medium text-primary-foreground",
        !active &&
          preview &&
          "border-primary bg-accent font-medium text-foreground",
        !active &&
          !preview &&
          estado === "cerrado" &&
          "border-border bg-secondary text-secondary-foreground",
        !active &&
          !preview &&
          estado === "abierto" &&
          "border-border bg-background text-foreground",
        !active &&
          !preview &&
          estado === "sin" &&
          "border-dashed border-border bg-background text-muted-foreground",
      )}
      style={{ width: CIERRE_CHIP_W, height: CIERRE_CHIP_H }}
    >
      {label}
    </div>
  );
}

export function CierreDiasCarousel({
  days,
  today,
  selectedYmd,
  onSelect,
  cierres,
  sucursalId,
}: CierreDiasCarouselProps) {
  const maxI = carouselMaxIndex(days.length);
  const showChrome = carouselShowChrome(days.length);
  const daysKey = days.join(",");
  const [index, setIndex] = useState(maxI);
  const [drag, setDrag] = useState(0);
  const [anim, setAnim] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const dragging = useRef(false);
  const moved = useRef(false);
  const dragRef = useRef(0);
  const downChipIndex = useRef(0);
  const skipButtonClick = useRef(false);

  useEffect(() => {
    setIndex(carouselMaxIndex(days.length));
    setDrag(0);
    setIsDragging(false);
  }, [daysKey, days.length]);

  const i = clampNumber(index, 0, maxI);
  const canLeft = showChrome && i > 0;
  const canRight = showChrome && i < maxI;
  const { peekLeft, peekRight } = carouselPeekFlags(i, maxI, showChrome);
  const viewportW = carouselViewportWidth(peekLeft, peekRight, days.length);
  const tx = carouselTranslateX(i, peekLeft, drag);
  const snapI = carouselSnapIndex(i, drag, maxI);
  const snapChipYmd = days[carouselAnchorIndex(snapI, days.length)] ?? "";
  const showPreview = showChrome && isDragging && Boolean(snapChipYmd);
  const slotLeft = carouselSlotLeft(peekLeft);

  const go = (dir: number) => {
    setAnim(true);
    setIndex((prev) => clampNumber(prev + dir, 0, carouselMaxIndex(days.length)));
  };

  const applyRelease = (dx: number, chipIndex: number) => {
    const result = carouselPointerRelease({
      days,
      index: i,
      dx,
      downChipIndex: chipIndex,
    });
    setIndex(result.nextIndex);
    if (result.selectYmd) onSelect(result.selectYmd);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!showChrome) return;
    dragging.current = true;
    moved.current = false;
    skipButtonClick.current = false;
    startX.current = event.clientX;
    dragRef.current = 0;
    const rect = event.currentTarget.getBoundingClientRect();
    downChipIndex.current = carouselChipIndexAtX(
      event.clientX - rect.left,
      i,
      peekLeft,
      0,
      days.length,
    );
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = event.clientX - startX.current;
    dragRef.current = dx;
    if (Math.abs(dx) > CIERRE_CHIP_CLICK_SLOP) {
      if (!moved.current) {
        moved.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        setAnim(false);
        setIsDragging(true);
      }
      setDrag(dx);
    }
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    const dx = dragRef.current;
    setDrag(0);
    dragRef.current = 0;
    setIsDragging(false);
    setAnim(true);
    skipButtonClick.current = Math.abs(dx) < CIERRE_CHIP_CLICK_SLOP;
    applyRelease(dx, downChipIndex.current);
    moved.current = false;
  };

  if (days.length === 0) return null;

  return (
    <div className="relative flex shrink-0 items-center gap-1.5">
      {showChrome ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 shrink-0 p-0"
          disabled={!canLeft}
          onClick={() => go(-1)}
          title="Días anteriores"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      ) : null}
      <div
        className={cn(
          "relative overflow-hidden",
          showChrome && (isDragging ? "cursor-grabbing" : "cursor-grab"),
        )}
        style={{
          width: viewportW,
          height: CIERRE_CHIP_H,
          touchAction: showChrome ? "none" : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          className="flex"
          style={{
            gap: CIERRE_CHIP_GAP,
            width: days.length * CIERRE_CHIP_STEP,
            transform: `translateX(${tx}px)`,
            transition: anim ? "transform 280ms ease" : "none",
            pointerEvents: isDragging ? "none" : undefined,
          }}
        >
          {days.map((ymd, chipIndex) => {
            const estado = cierreEstadoOf(cierres, ymd, sucursalId);
            const label = cierreChipLabel(ymd, today);
            return (
              <button
                key={ymd}
                type="button"
                draggable={false}
                className="shrink-0 border-0 bg-transparent p-0"
                onClick={() => {
                  if (skipButtonClick.current) {
                    skipButtonClick.current = false;
                    return;
                  }
                  applyRelease(0, chipIndex);
                }}
              >
                <DiaChip
                  label={label}
                  estado={estado}
                  active={!showPreview && selectedYmd === ymd}
                  preview={showPreview && snapChipYmd === ymd}
                />
              </button>
            );
          })}
        </div>
        {showPreview ? (
          <div
            className="pointer-events-none absolute top-0 box-border rounded-md border-2 border-primary"
            style={{
              left: slotLeft,
              width: CIERRE_CHIP_W,
              height: CIERRE_CHIP_H,
            }}
          />
        ) : null}
      </div>
      {showChrome ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 shrink-0 p-0"
          disabled={!canRight}
          onClick={() => go(1)}
          title="Hacia hoy"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
